<script setup lang="ts">
import { computed } from 'vue'
import { storeToRefs } from 'pinia'
import DOMPurify from 'dompurify'
import WikiText from '@/components/wikitext/WikiText.vue'
import CopyTrackWrapper from '@/components/CopyTrackWrapper.vue'
import { useUserStore } from '@/authentication/user.store'
import { ACTION_TYPES, CONTENT_TYPES, actionPermissions, blurContentPermissions } from '@/util/access_visibility'
import type { WikiTextLockMode } from '@/components/wikitext/wiki_text_types'
import type { StrategyPanel } from '../strategy_types'
import { LIGHT_STRATEGY_TEXT } from '../strategy_text_classes'

const props = withDefaults(defineProps<{
  title: string
  panelData: StrategyPanel[]
  overwriteBlur?: boolean
}>(), {
  overwriteBlur: false,
})

const { authInfo } = storeToRefs(useUserStore())

const shouldBlur = computed(() =>
  blurContentPermissions(CONTENT_TYPES.STRATEGY, {}, { overwriteBlur: props.overwriteBlur, ...authInfo.value }).blur
)
const shouldBlockLink = computed(() =>
  actionPermissions(ACTION_TYPES.FOLLOW_LINK, CONTENT_TYPES.STRATEGY, {}, authInfo.value).block
)
const lockMode = computed((): WikiTextLockMode => {
  if (shouldBlur.value) return 'completely_blur'
  if (shouldBlockLink.value) return 'lock_links'
  return 'default'
})
const locked = computed(() => lockMode.value !== 'default')

// Panel titles are the first line of the tip or example text, which authors mark up with <b>.
const sanitizedTitle = (title: string) => DOMPurify.sanitize(title)
</script>

<template>
  <div class="text-gray-900">
    <h2 v-if="panelData.length > 0" class="mb-2.5 mt-5 text-display-md font-bold leading-[1.1]">{{ title }}</h2>
    <CopyTrackWrapper
      v-for="(panel, index) in panelData"
      :key="index"
      :type="title"
      :title="panel.title"
      gate-copy
      class="mb-5 rounded-xs border border-[#ddd] bg-white text-md leading-5 shadow-[0_1px_1px_rgba(0,0,0,0.05)]"
    >
      <div class="rounded-t-[3px] border-b border-[#ddd] bg-gray-100 px-6 py-2.5 text-lg leading-[1.428571]">
        <h3 class="text-lg font-bold leading-[1.1] [&_b]:font-bold" v-html="sanitizedTitle(panel.title)" />
      </div>
      <div :class="['px-6 py-4 text-lg leading-[25px]', LIGHT_STRATEGY_TEXT]">
        <WikiText :text="panel.text" :lock-mode="lockMode" :locked="locked" />
      </div>
    </CopyTrackWrapper>
  </div>
</template>
